import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { TillPaymentMethodCode } from '@efacture/core';
import {
  DASHBOARD_CATALOGUE_PREFERENCE_KEYS,
  DASHBOARD_WIDGETS_PREFERENCE_KEY,
  NAV_DISABLED_KEY,
  NAV_HIDDEN_KEY,
  moduleDisabledForUrl,
  navDisabledIds,
  navHiddenIds,
  type DashboardCatalogueId,
  type DashboardWidgetKey,
} from '@efacture/core/sector';
import { apiClient } from '@/api/client';
import { getActiveUserId } from '@/lib/active-tenant';
import { cacheReferenceData, getCachedReferenceData, type ReferenceCacheKind } from '@/lib/offline-store';
import { tableColumnsKey } from '@/components/data-table/columns';

/** GET /api/ui-preferences/pos-payment-methods */
export interface PosPaymentMethods {
  /** The company's setting: every till method when it set none. */
  accepted: TillPaymentMethodCode[];
  /** `accepted` minus the methods nothing is configured for. */
  methods: TillPaymentMethodCode[];
  /** The Mobile Money operators the till offers. */
  mobileMoneyOperators: string[];
  /** Whether the company's PI-SPI configuration is enabled. */
  pispiEnabled: boolean;
}

/** A key's stored values and the one that applies (GET /api/ui-preferences). */
export interface ResolvedUiPreference<T> {
  company: T | null;
  user: T | null;
  value: T | null;
}

export const POS_PAYMENT_METHODS_KEY = 'pos.paymentMethods';

export const uiPreferenceKeys = {
  all: ['ui-preferences'] as const,
  posPaymentMethods: () => [...uiPreferenceKeys.all, 'pos-payment-methods'] as const,
  values: (keys: readonly string[]) => [...uiPreferenceKeys.all, 'values', ...keys] as const,
  nav: () => [...uiPreferenceKeys.all, 'nav'] as const,
  navCached: () => [...uiPreferenceKeys.all, 'nav-cached'] as const,
  tableColumns: (tableId: string) => [...uiPreferenceKeys.all, 'table-columns', tableId] as const,
};

/**
 * The payment methods the till offers. Each answer is kept for the tenant in
 * the offline store; when the request fails (offline, or the server cannot
 * answer) the last kept answer is returned, and the error only when none was
 * ever kept.
 */
export async function fetchPosPaymentMethods(): Promise<PosPaymentMethods> {
  try {
    const methods = await apiClient.get<PosPaymentMethods>('/ui-preferences/pos-payment-methods');
    await cacheReferenceData('payment-methods', methods).catch(() => undefined);
    return methods;
  } catch (error) {
    const cached = await getCachedReferenceData<PosPaymentMethods>('payment-methods').catch(() => undefined);
    if (cached) return cached.data;
    throw error;
  }
}

export function usePosPaymentMethods() {
  return useQuery({
    queryKey: uiPreferenceKeys.posPaymentMethods(),
    queryFn: fetchPosPaymentMethods,
    staleTime: 1000 * 60 * 5,
  });
}

/** The company and own values of `keys`. */
export function useUiPreferences<T>(keys: readonly string[]) {
  return useQuery({
    queryKey: uiPreferenceKeys.values(keys),
    queryFn: () =>
      apiClient.get<{ preferences: Record<string, ResolvedUiPreference<T>> }>('/ui-preferences', {
        keys: keys.join(','),
      }),
  });
}

/** Writes the company value of `key` (`tenant:settings`). */
export function useSetCompanyUiPreference<T>(key: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (value: T) =>
      apiClient.put<{ preference: { value: T; version: number } }>(
        `/ui-preferences/company/${encodeURIComponent(key)}`,
        { value }
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: uiPreferenceKeys.all }),
  });
}

/** The nav entries the company switched off and the ones the user hid. */
export interface NavPreferences {
  companyDisabled: string[];
  userHidden: string[];
}

/**
 * GET /api/ui-preferences for `nav.disabled` and `nav.hidden`. The company's
 * switched-off entries are kept for the tenant in the offline store; when
 * the request fails the last kept set is returned with nothing hidden, and
 * the error only when none was ever kept. The user's hidden items are not
 * kept: the store is per tenant, not per user.
 */
export async function fetchNavPreferences(): Promise<NavPreferences> {
  try {
    const { preferences } = await apiClient.get<{
      preferences: Record<string, ResolvedUiPreference<unknown>>;
    }>('/ui-preferences', { keys: `${NAV_DISABLED_KEY},${NAV_HIDDEN_KEY}` });
    const companyDisabled = navDisabledIds(preferences[NAV_DISABLED_KEY]?.company ?? null);
    const userHidden = navHiddenIds(preferences[NAV_HIDDEN_KEY]?.user ?? null);
    await cacheReferenceData('nav-disabled', companyDisabled).catch(() => undefined);
    return { companyDisabled, userHidden };
  } catch (error) {
    const cached = await readCachedNavDisabled();
    if (cached !== null) return { companyDisabled: cached, userHidden: [] };
    throw error;
  }
}

/** The company's switched-off entries last kept for the tenant, or null. */
export async function readCachedNavDisabled(): Promise<string[] | null> {
  const cached = await getCachedReferenceData<unknown>('nav-disabled').catch(() => undefined);
  return cached ? navDisabledIds(cached.data) : null;
}

/**
 * The menu's company and user layers. `companyDisabled` is `null` only
 * while no value was ever loaded for the tenant: the kept set applies from
 * the first render it is read, before the request answers.
 */
export function useNavPreferences(): { companyDisabled: string[] | null; userHidden: string[] } {
  const live = useQuery({
    queryKey: uiPreferenceKeys.nav(),
    queryFn: fetchNavPreferences,
    staleTime: 1000 * 60 * 5,
  });
  const kept = useQuery({
    queryKey: uiPreferenceKeys.navCached(),
    queryFn: readCachedNavDisabled,
    staleTime: Infinity,
  });
  return {
    companyDisabled: live.data?.companyDisabled ?? kept.data ?? null,
    userHidden: live.data?.userHidden ?? [],
  };
}

/**
 * Hides an item from the user's own menu. The new list is built from the
 * latest one in the cache when the write runs, and writes run one after the
 * other, so two quick hides keep both.
 */
export function useNavHiddenWriter() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    scope: { id: 'nav-hidden' },
    mutationFn: (change: (current: string[]) => string[]) => {
      const current = queryClient.getQueryData<NavPreferences>(uiPreferenceKeys.nav())?.userHidden ?? [];
      const next = change(current);
      queryClient.setQueryData<NavPreferences>(uiPreferenceKeys.nav(), (old) =>
        old ? { ...old, userHidden: next } : old
      );
      return apiClient.put(`/ui-preferences/me/${encodeURIComponent(NAV_HIDDEN_KEY)}`, { value: next });
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: uiPreferenceKeys.nav() }),
  });
  return {
    hide: (key: string, options?: { onError?: () => void }) =>
      mutation.mutate((current) => [...current.filter((hidden) => hidden !== key), key], options),
    showAll: (options?: { onError?: () => void }) => mutation.mutate(() => [], options),
  };
}

/**
 * Whether the company left an API route open: false once the switch that
 * closes it (`nav.disabled`) is known to be off. A screen reading another
 * module's route asks this before querying it, and hides or explains the
 * part that needs it.
 */
export function useRouteOpen(): (route: string) => boolean {
  const { companyDisabled } = useNavPreferences();
  const disabled = new Set(companyDisabled ?? []);
  return (route) => !moduleDisabledForUrl(route, disabled);
}

/** Writes the session user's own value of `key`. */
export function useSetOwnUiPreference<T>(key: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (value: T) =>
      apiClient.put<{ preference: { value: T; version: number } }>(
        `/ui-preferences/me/${encodeURIComponent(key)}`,
        { value }
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: uiPreferenceKeys.all }),
  });
}

/** Removes the session user's own value of `key`: the company value applies again. */
export function useClearOwnUiPreference(key: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiClient.delete<{ key: string }>(`/ui-preferences/me/${encodeURIComponent(key)}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: uiPreferenceKeys.all }),
  });
}

export { DASHBOARD_WIDGETS_PREFERENCE_KEY as DASHBOARD_WIDGETS_KEY };

/** The company default and the user's own choice of dashboard widgets. */
export type DashboardWidgetPreference = ResolvedUiPreference<DashboardWidgetKey[]>;

interface CachedDashboardWidgetPreference {
  /** The user the snapshot was read for: a snapshot is never handed to another user of the same tenant. */
  userId: string | null;
  preference: DashboardWidgetPreference;
}

const NO_WIDGET_PREFERENCE: DashboardWidgetPreference = { company: null, user: null, value: null };

/**
 * The dashboard-widget preference. Each answer is kept for the tenant and
 * user in the offline store; when the request fails the last answer kept
 * for the same user is returned, else no preference (the role's widgets).
 */
export async function fetchDashboardWidgetPreference(): Promise<DashboardWidgetPreference> {
  const userId = getActiveUserId();
  try {
    const { preferences } = await apiClient.get<{
      preferences: Record<string, DashboardWidgetPreference>;
    }>('/ui-preferences', { keys: DASHBOARD_WIDGETS_PREFERENCE_KEY });
    const preference = preferences[DASHBOARD_WIDGETS_PREFERENCE_KEY] ?? NO_WIDGET_PREFERENCE;
    const snapshot: CachedDashboardWidgetPreference = { userId, preference };
    await cacheReferenceData('dashboard-widgets', snapshot).catch(() => undefined);
    return preference;
  } catch {
    const cached = await getCachedReferenceData<CachedDashboardWidgetPreference>('dashboard-widgets').catch(
      () => undefined
    );
    if (cached && userId !== null && cached.data.userId === userId) return cached.data.preference;
    return NO_WIDGET_PREFERENCE;
  }
}

export function useDashboardWidgetPreference() {
  return useQuery({
    queryKey: [...uiPreferenceKeys.all, 'dashboard-widgets'] as const,
    queryFn: fetchDashboardWidgetPreference,
    staleTime: 1000 * 60 * 5,
  });
}

/** The company default and the user's own choice of one dashboard's widgets (accounting, firm, stock). */
export type CatalogueWidgetPreference = ResolvedUiPreference<string[]>;

const NO_CATALOGUE_PREFERENCE: CatalogueWidgetPreference = { company: null, user: null, value: null };

const CATALOGUE_CACHE_KIND: Record<DashboardCatalogueId, ReferenceCacheKind> = {
  accounting: 'dashboard-widgets-accounting',
  firm: 'dashboard-widgets-firm',
  stock: 'dashboard-widgets-stock',
};

interface CachedCataloguePreference {
  userId: string | null;
  preference: CatalogueWidgetPreference;
}

/**
 * One dashboard's widget preference, kept per tenant and user in the
 * offline store like `fetchDashboardWidgetPreference`: when the request
 * fails the last answer kept for the same user is returned, else none.
 */
export async function fetchCataloguePreference(id: DashboardCatalogueId): Promise<CatalogueWidgetPreference> {
  const key = DASHBOARD_CATALOGUE_PREFERENCE_KEYS[id];
  const kind = CATALOGUE_CACHE_KIND[id];
  const userId = getActiveUserId();
  try {
    const { preferences } = await apiClient.get<{
      preferences: Record<string, CatalogueWidgetPreference>;
    }>('/ui-preferences', { keys: key });
    const preference = preferences[key] ?? NO_CATALOGUE_PREFERENCE;
    const snapshot: CachedCataloguePreference = { userId, preference };
    await cacheReferenceData(kind, snapshot).catch(() => undefined);
    return preference;
  } catch {
    const cached = await getCachedReferenceData<CachedCataloguePreference>(kind).catch(() => undefined);
    if (cached && userId !== null && cached.data.userId === userId) return cached.data.preference;
    return NO_CATALOGUE_PREFERENCE;
  }
}

export function useCataloguePreference(id: DashboardCatalogueId) {
  return useQuery({
    queryKey: [...uiPreferenceKeys.all, 'dashboard-widgets', id] as const,
    queryFn: () => fetchCataloguePreference(id),
    staleTime: 1000 * 60 * 5,
  });
}

/** The stored column choices of a table: the company default and the user's own. */
export interface TableColumnPreferences {
  company: unknown;
  user: unknown;
}

/** Where a user's column choice for `tableId` is kept offline; null without a session user. */
function tableColumnsCacheKind(tableId: string): ReferenceCacheKind | null {
  const userId = getActiveUserId();
  return userId ? `table-columns:${userId}:${tableId}` : null;
}

/**
 * The column choices of `tableId`. Each answer is kept for the tenant and the
 * user in the offline store; when the request fails the last kept answer is
 * returned, and the error only when none was ever kept.
 */
export async function fetchTableColumnPreferences(tableId: string): Promise<TableColumnPreferences> {
  const key = tableColumnsKey(tableId);
  const kind = tableColumnsCacheKind(tableId);
  try {
    const { preferences } = await apiClient.get<{ preferences: Record<string, ResolvedUiPreference<unknown>> }>(
      '/ui-preferences',
      { keys: key }
    );
    const stored = preferences[key];
    const answer: TableColumnPreferences = { company: stored?.company ?? null, user: stored?.user ?? null };
    if (kind) await cacheReferenceData(kind, answer).catch(() => undefined);
    return answer;
  } catch (error) {
    const cached = kind ? await getCachedReferenceData<TableColumnPreferences>(kind).catch(() => undefined) : undefined;
    if (cached) return cached.data;
    throw error;
  }
}

export function useTableColumnPreferences(tableId: string) {
  return useQuery({
    queryKey: uiPreferenceKeys.tableColumns(tableId),
    queryFn: () => fetchTableColumnPreferences(tableId),
    staleTime: 1000 * 60 * 5,
  });
}

/** Writes the session user's own column choice of `tableId`. */
export function useSetOwnTableColumns(tableId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (columns: string[]) =>
      apiClient.put(`/ui-preferences/me/${encodeURIComponent(tableColumnsKey(tableId))}`, { value: { columns } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: uiPreferenceKeys.tableColumns(tableId) }),
  });
}

/** Writes the company's column default of `tableId` (`tenant:settings`). */
export function useSetCompanyTableColumns(tableId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (columns: string[]) =>
      apiClient.put(`/ui-preferences/company/${encodeURIComponent(tableColumnsKey(tableId))}`, { value: { columns } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: uiPreferenceKeys.tableColumns(tableId) }),
  });
}
